import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCheckListingStatus } from "@/hooks/use-refresh-listing-status";
import { useWorkspace } from "@/hooks/use-workspace";
import { MockProperty } from "@/data/mockProperties";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";
import { buildSeedDealSettings } from "@/lib/deal-seed";
import {
  normalizeListingStatus,
  type ListingStatus,
} from "@/lib/listing-status";
import { findWatchlistPipeline } from "@/lib/pipeline-display";
import {
  splitStrategySettings,
  type StrategyPatch,
} from "@/lib/property-setting";
import { STAGE_HISTORY_QUERY_PREFIX } from "@/lib/query-keys";
import type { PipelineRow, Strategy } from "@/types/pipeline";

export interface SavedProperty {
  id: string;
  created_by: string;
  workspace_id: string;
  property_id: string;
  property_data: MockProperty;
  notes: string | null;
  pipeline_id: string | null;
  purchase_price: number | null;
  created_at: string;
  updated_at: string;
  /**
   * Pre-acquisition market status from the feed ('active' … 'sold'). Optional
   * because rows cached before the column landed won't carry it — read it
   * through `resolveListingStatus` rather than directly.
   */
  listing_status?: string | null;
  /** When the property API last confirmed `listing_status`. */
  listing_status_checked_at?: string | null;
}

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

/** Verdict from the pre-write listing check. */
interface ListingGate {
  /** False only for a confirmed sale — the write must be abandoned. */
  proceed: boolean;
  /** Status the feed confirmed, when it could resolve one. */
  status?: ListingStatus;
  /** Server time of that confirmation. Only set alongside `status`. */
  checkedAt?: string;
}

/**
 * Upsert helper for property_setting — used by addToPipeline when the
 * caller passes initial `settings` so the user's current deal inputs are
 * persisted at promotion time. Returns the error on failure, null on
 * success. Cast at the call boundary since the generated Database type
 * doesn't yet include the new property_setting shape.
 *
 * `settings.rehab` is treated as a diff payload (`{insert, update, delete}`)
 * — it's peeled off before writing to property_setting and applied against
 * the separate `rehab_line_items` table.
 */
async function upsertPropertySetting(args: {
  pipelinePropertyId: string;
  workspaceId: string;
  createdBy: string;
  settings: Record<string, unknown>;
}): Promise<unknown | null> {
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (s: string) => {
        eq: (col: string, v: string) => {
          maybeSingle: () => DbResp<{ id: string } | null>;
        };
      };
      update: (v: unknown) => {
        eq: (col: string, v: string) => DbResp<unknown>;
      };
      insert: (v: unknown) => DbResp<unknown>;
    };
  };

  // Peel rehab diff off the patch — it's written to rehab_line_items, not
  // property_setting. Defensive narrowing: anything we can't recognize is
  // ignored rather than crashing the promote flow.
  const { rehab: rehabRaw, ...settingsRest } = args.settings as {
    rehab?: unknown;
  } & Record<string, unknown>;

  // Split the nested strategy objects (flip/brrrr/rental) into the dedicated
  // property_setting columns, same as useDealSettings.saveSettings — so the
  // promote-to-pipeline path never persists the fat legacy blobs.
  const { flip, brrrr, rental, ...scalarOnly } = settingsRest as StrategyPatch &
    Record<string, unknown>;
  const settingsOnly: Record<string, unknown> = {
    ...scalarOnly,
    ...splitStrategySettings({ flip, brrrr, rental }),
  };
  const rehabDiff = (rehabRaw && typeof rehabRaw === "object"
    ? (rehabRaw as {
        insert?: Array<{
          category: "standard" | "custom";
          label: string;
          amount: number;
        }>;
        update?: Array<{ id: string; label: string; amount: number }>;
        delete?: string[];
      })
    : null);

  const { data: existing, error: lookupErr } = await client
    .from("property_setting")
    .select("id")
    .eq("pipeline_property_id", args.pipelinePropertyId)
    .maybeSingle();
  if (lookupErr) return lookupErr;

  if (existing?.id) {
    const { error } = await client
      .from("property_setting")
      .update(settingsOnly)
      .eq("id", existing.id);
    if (error) return error;
  } else {
    const { error } = await client.from("property_setting").insert({
      ...settingsOnly,
      pipeline_property_id: args.pipelinePropertyId,
      workspace_id: args.workspaceId,
      created_by: args.createdBy,
    });
    if (error) return error;
  }

  // Apply the rehab diff. For the promote-to-pipeline flow there's no
  // existing row yet, so only `insert` is realistically populated — but we
  // honor all three buckets so this helper stays usable if other callers
  // ever pass an update/delete patch.
  if (rehabDiff?.insert?.length) {
    const insertRows = rehabDiff.insert.map((entry) => ({
      workspace_id: args.workspaceId,
      pipeline_property_id: args.pipelinePropertyId,
      created_by: args.createdBy,
      category: entry.category,
      label: entry.label,
      estimated_cost: entry.amount,
    }));
    const { error } = await (
      supabase as unknown as {
        from: (t: string) => { insert: (v: unknown) => DbResp<unknown> };
      }
    )
      .from("rehab_line_items")
      .insert(insertRows);
    if (error) return error;
  }
  if (rehabDiff?.update?.length) {
    for (const entry of rehabDiff.update) {
      const { error } = await supabase
        .from("rehab_line_items")
        .update({
          label: entry.label,
          estimated_cost: entry.amount,
        } as never)
        .eq("id", entry.id);
      if (error) return error;
    }
  }
  if (rehabDiff?.delete?.length) {
    const { error } = await supabase
      .from("rehab_line_items")
      .delete()
      .in("id", rehabDiff.delete);
    if (error) return error;
  }

  return null;
}

/**
 * Create the `property_setting` row for a deal that was just promoted into a
 * pipeline without the detail view being open (the New Deal drawer). The
 * "Add to Pipeline" button gets its values from the mounted detail view; here
 * we derive the same defaults from the property snapshot via
 * `buildSeedDealSettings` so both entry points leave a deal with settings.
 *
 * Idempotent by design:
 *   - a existing `property_setting` row means the user already saved inputs
 *     while the property was only heart-saved → leave it untouched,
 *   - the default rehab scope is only inserted when the deal has no
 *     `rehab_line_items` rows, so an edited scope is never duplicated.
 *
 * Returns the error on failure, null on success/no-op — the caller reports it
 * without failing the promotion itself.
 */
async function seedPropertySetting(args: {
  pipelinePropertyId: string;
  workspaceId: string;
  createdBy: string;
  property: MockProperty;
  strategy: Strategy;
}): Promise<unknown | null> {
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (s: string) => {
        eq: (
          col: string,
          v: string,
        ) => {
          maybeSingle: () => DbResp<{ id: string } | null>;
          limit: (n: number) => DbResp<Array<{ id: string }>>;
        };
      };
    };
  };

  const { data: existing, error: lookupErr } = await client
    .from("property_setting")
    .select("id")
    .eq("pipeline_property_id", args.pipelinePropertyId)
    .maybeSingle();
  if (lookupErr) return lookupErr;
  if (existing?.id) return null;

  const { data: rehabRows, error: rehabErr } = await client
    .from("rehab_line_items")
    .select("id")
    .eq("pipeline_property_id", args.pipelinePropertyId)
    .limit(1);
  if (rehabErr) return rehabErr;

  return upsertPropertySetting({
    pipelinePropertyId: args.pipelinePropertyId,
    workspaceId: args.workspaceId,
    createdBy: args.createdBy,
    settings: buildSeedDealSettings(args.property, args.strategy, {
      includeRehabScope: (rehabRows ?? []).length === 0,
    }),
  });
}

export function useSavedProperties() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Reuse the same query key as usePipeline so the pipelines list is fetched
  // once per workspace and shared across hooks via the React Query cache.
  const pipelinesQuery = useQuery({
    queryKey: ["pipelines", currentWorkspace?.id] as const,
    enabled: Boolean(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return [] as PipelineRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (col: string, v: string) => {
                order: (col: string) => DbResp<PipelineRow[]>;
              };
            };
          };
        }
      )
        .from("pipelines")
        .select("*")
        .eq("workspace_id", currentWorkspace.id)
        .order("order")) as PostgrestSingleResponse<PipelineRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  const pipelines: PipelineRow[] = useMemo(
    () => pipelinesQuery.data ?? [],
    [pipelinesQuery.data],
  );
  const watchlist = useMemo(
    () => findWatchlistPipeline(pipelines),
    [pipelines],
  );

  /* ──────────────────────────────────────────────────────────────────── *
   *  Saved-properties list — TanStack Query-backed.
   *
   *  Previously held in `useState` + a manual `fetchSavedProperties` that
   *  every mutation `await`-ed. That made `invalidateQueries` on this key
   *  a silent no-op (the kanban / pipeline pages were out of sync until
   *  refresh). Now the list lives in the cache under
   *  `["pipeline_properties", workspaceId]` — every consumer reacts to
   *  invalidations or direct `setQueryData` writes.
   * ──────────────────────────────────────────────────────────────────── */
  const savedPropertiesKey = useMemo(
    () => ["pipeline_properties", currentWorkspace?.id] as const,
    [currentWorkspace?.id],
  );

  const savedPropertiesQuery = useQuery({
    queryKey: savedPropertiesKey,
    enabled: Boolean(user?.id && currentWorkspace?.id),
    queryFn: async () => {
      if (!user?.id || !currentWorkspace?.id) return [] as SavedProperty[];
      const { data, error } = await supabase
        .from("pipeline_properties")
        .select("*")
        .eq("workspace_id", currentWorkspace.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((item) => ({
        ...item,
        property_data: item.property_data as unknown as MockProperty,
      })) as SavedProperty[];
    },
  });

  const savedProperties: SavedProperty[] = useMemo(
    () => savedPropertiesQuery.data ?? [],
    [savedPropertiesQuery.data],
  );

  const savedPropertyIds = useMemo(
    () => new Set(savedProperties.map((p) => p.property_id)),
    [savedProperties],
  );

  const isLoading = savedPropertiesQuery.isLoading || pipelinesQuery.isLoading;

  const invalidateSaved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: savedPropertiesKey });
  }, [queryClient, savedPropertiesKey]);

  /**
   * Refresh the stage-history cache (`usePipeline().stageHistory`) after we
   * append an audit row. Prefix match — the full key carries the deal-id set,
   * which changes as deals are added.
   */
  const invalidateStageHistory = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: STAGE_HISTORY_QUERY_PREFIX,
    });
  }, [queryClient]);

  const isSaved = useCallback(
    (propertyId: string) => savedPropertyIds.has(propertyId),
    [savedPropertyIds],
  );

  const { mutateAsync: checkListingStatus } = useCheckListingStatus();

  /**
   * Ask the feed whether a property is still for sale, *before* writing it.
   *
   * Every entry point seeds `listing_status` from the search snapshot, which can
   * be hours or days stale by the time someone acts on it. Running the check
   * first means a sold listing never reaches the table at all, rather than
   * landing and being flagged after the fact.
   *
   * When it clears, the caller writes the freshly confirmed status and the
   * server's timestamp, so the row starts out verified instead of guessed.
   *
   * Never throws, and an unreadable answer proceeds. Absence from the feed is
   * ambiguous — sold, delisted, or just not on this page of results — and an
   * API hiccup must not stop someone recording a real opportunity. Only a
   * confirmed sale refuses.
   */
  const ensureNotSold = useCallback(
    async (property: MockProperty): Promise<ListingGate> => {
      try {
        const result = await checkListingStatus({
          propertyId: property.id,
          property: property as unknown as Record<string, unknown>,
        });

        if (result.sold) {
          toast.error("This property has already sold", {
            description: `${result.address || property.address} is no longer on the market, so it wasn't added.`,
          });
          return { proceed: false };
        }

        if (result.unresolved || !result.status) return { proceed: true };

        return {
          proceed: true,
          status: result.status,
          checkedAt: result.checkedAt,
        };
      } catch (err) {
        console.error("Listing status check failed:", err);
        return { proceed: true };
      }
    },
    [checkListingStatus],
  );

  const saveProperty = useCallback(
    async (property: MockProperty) => {
      if (!user?.id || !currentWorkspace?.id) {
        toast.error("Please log in to save properties");
        return false;
      }

      // Flow 1 of 3 — the heart icon. Check before writing so a sold listing
      // never reaches the table.
      const gate = await ensureNotSold(property);
      if (!gate.proceed) return false;

      try {
        const insertData = {
          created_by: user.id,
          workspace_id: currentWorkspace.id,
          property_id: property.id,
          property_data: property as unknown as Record<string, unknown>,
          purchase_price: property.price,
          pipeline_id: null,
          // Prefer what the check just confirmed over the search snapshot, and
          // only claim a checked_at when the API actually answered — NULL puts
          // the row at the front of the refresh sweep's queue.
          listing_status: gate.status ?? normalizeListingStatus(property.status),
          listing_status_checked_at: gate.checkedAt ?? null,
        };

        const { data: inserted, error } = await supabase
          .from("pipeline_properties")
          .insert(insertData as never)
          .select("id")
          .single();

        if (error) throw error;

        if (inserted?.id) {
          const { error: histError } = await supabase
            .from("pipeline_stage_history")
            .insert({
              pipeline_property_id: inserted.id,
              changed_by: user.id,
              from_pipeline_id: null,
              to_pipeline_id: null,
              workspace_id: currentWorkspace.id,
            } as never);
          if (histError) {
            console.error("pipeline_stage_history insert:", histError);
          } else {
            invalidateStageHistory();
          }
        }

        toast.success("Property saved");
        invalidateSaved();
        return true;
      } catch (error: unknown) {
        const err = error as { code?: string };
        if (err.code === "23505") {
          toast.info("Property is already saved");
        } else {
          console.error("Error saving property:", error);
          toast.error(toUserMessage(error, "Failed to save property"));
        }
        return false;
      }
    },
    [
      user?.id,
      currentWorkspace?.id,
      invalidateSaved,
      invalidateStageHistory,
      ensureNotSold,
    ],
  );

  const unsaveProperty = useCallback(
    async (propertyId: string) => {
      if (!user?.id || !currentWorkspace?.id) return false;

      try {
        const { data, error } = await supabase
          .from("pipeline_properties")
          .delete()
          .eq("workspace_id", currentWorkspace.id)
          .eq("property_id", propertyId)
          .select("id");

        if (error) throw error;
        assertRowsAffected(data, "remove this property from your saved list");

        // Optimistically drop the row from the cache for instant UI feedback;
        // a final invalidate confirms against the server.
        queryClient.setQueryData<SavedProperty[]>(savedPropertiesKey, (prev) =>
          (prev ?? []).filter((p) => p.property_id !== propertyId),
        );
        invalidateSaved();
        // Not "watchlist" — a heart-saved row has pipeline_id NULL, so it was
        // never in the Watchlist stage. It came off Saved Properties.
        toast.success("Property removed from Saved Properties");
        return true;
      } catch (error) {
        console.error("Error removing property:", error);
        toast.error(toUserMessage(error, "Failed to remove property"));
        return false;
      }
    },
    [
      user?.id,
      currentWorkspace?.id,
      queryClient,
      savedPropertiesKey,
      invalidateSaved,
    ],
  );

  /**
   * Delete a pipeline deal by its `pipeline_properties.id`.
   *
   * Distinct from `unsaveProperty`, which keys on `property_id` and would take
   * out every row for that property in the workspace — here we remove one
   * specific deal card. Used by the kanban's remove action on listings that sold
   * to another buyer.
   *
   * Child rows cascade (see 20260804010000_pipeline_properties_delete_rules.sql);
   * manual accounting transactions survive with their property link nulled.
   */
  const removeDeal = useCallback(
    async (pipelinePropertyId: string) => {
      if (!user?.id || !currentWorkspace?.id) return false;

      try {
        const { data, error } = await supabase
          .from("pipeline_properties")
          .delete()
          .eq("workspace_id", currentWorkspace.id)
          .eq("id", pipelinePropertyId)
          .select("id");

        if (error) throw error;
        assertRowsAffected(data, "remove this deal from the pipeline");

        // Drop it from the cache immediately, then confirm against the server.
        queryClient.setQueryData<SavedProperty[]>(savedPropertiesKey, (prev) =>
          (prev ?? []).filter((p) => p.id !== pipelinePropertyId),
        );
        invalidateSaved();
        invalidateStageHistory();
        toast.success("Deal removed from pipeline");
        return true;
      } catch (error) {
        // A 23503 here means some child table still blocks the delete — i.e. a
        // FK we haven't given a delete rule. Say so plainly instead of showing
        // a raw Postgres constraint name.
        const code = (error as { code?: string })?.code;
        if (code === "23503") {
          toast.error(
            "Can't remove this deal — related records are still linked to it. " +
              "Delete those first, or contact support.",
          );
        } else {
          toast.error(toUserMessage(error, "Failed to remove deal"));
        }
        return false;
      }
    },
    [
      user?.id,
      currentWorkspace?.id,
      queryClient,
      savedPropertiesKey,
      invalidateSaved,
      invalidateStageHistory,
    ],
  );

  const toggleSaved = useCallback(
    async (property: MockProperty) => {
      if (isSaved(property.id)) {
        return unsaveProperty(property.id);
      } else {
        return saveProperty(property);
      }
    },
    [isSaved, saveProperty, unsaveProperty],
  );

  /**
   * Insert or update `pipeline_properties` so the property appears in
   * Pipeline with the latest snapshot. If the row is currently in
   * Closed Lost, reactivate it by moving it back to Watchlist.
   *
   * Optional `options.strategy` is folded into the same write so the
   * property_properties row lands with its strategy set in one round-trip
   * (no separate `promoteToPipeline` call needed).
   *
   * Optional `options.settings` is upserted into the `property_setting`
   * table for the new/updated pipeline_property_id. This lets the detail
   * page persist the user's current deal inputs at the moment they click
   * "Add to Pipeline", rather than asking them to click Save afterwards.
   */
  const addToPipeline = useCallback(
    async (
      property: MockProperty,
      options?: {
        strategy?: "BRRRR" | "Flip" | "Rental";
        settings?: Record<string, unknown>;
      },
    ): Promise<string | null> => {
      if (!user?.id || !currentWorkspace?.id) {
        toast.error("Please log in to add properties to your pipeline");
        return null;
      }

      // Flow 2 of 3 — the "Add to Pipeline" button. Gate both branches below.
      const gate = await ensureNotSold(property);
      if (!gate.proceed) return null;

      const existing = savedProperties.find(
        (p) => p.property_id === property.id,
      );
      const purchase = property.price;
      const listingFields = {
        listing_status: gate.status ?? normalizeListingStatus(property.status),
        listing_status_checked_at: gate.checkedAt ?? null,
      };
      const strategyField = options?.strategy
        ? { strategy: options.strategy }
        : {};

      let pipelinePropertyId: string | null = null;

      try {
        if (existing) {
          const currentPipeline = pipelines.find(
            (p) => p.id === existing.pipeline_id,
          );
          const reactivate =
            currentPipeline?.kind === "closed_lost" && Boolean(watchlist?.id);
          const promoteFromSaved =
            !existing.pipeline_id && Boolean(watchlist?.id);
          const shouldMove = reactivate || promoteFromSaved;
          const nextPipelineId = shouldMove
            ? (watchlist?.id ?? existing.pipeline_id)
            : existing.pipeline_id;

          const { data: updatedRows, error: updateError } = await supabase
            .from("pipeline_properties")
            .update({
              property_data: property as unknown as Record<string, unknown>,
              purchase_price: purchase,
              pipeline_id: nextPipelineId,
              ...listingFields,
              ...strategyField,
            } as never)
            .eq("id", existing.id)
            .select("id");

          if (updateError) throw updateError;
          assertRowsAffected(updatedRows, "update this pipeline item");

          if (shouldMove) {
            const { error: histError } = await supabase
              .from("pipeline_stage_history")
              .insert({
                pipeline_property_id: existing.id,
                changed_by: user.id,
                from_pipeline_id: existing.pipeline_id,
                to_pipeline_id: nextPipelineId,
                workspace_id: currentWorkspace.id,
              } as never);
            if (histError)
              console.error("pipeline_stage_history insert:", histError);
            else invalidateStageHistory();
          }

          toast.success(
            promoteFromSaved
              ? "Added to pipeline"
              : reactivate
                ? "Updated in pipeline and moved to Watchlist"
                : "Pipeline entry updated with your latest numbers",
          );

          pipelinePropertyId = existing.id;
        } else {
          const { data: inserted, error: insertError } = await supabase
            .from("pipeline_properties")
            .insert({
              created_by: user.id,
              workspace_id: currentWorkspace.id,
              property_id: property.id,
              property_data: property as unknown as Record<string, unknown>,
              purchase_price: purchase,
              pipeline_id: watchlist?.id ?? null,
              ...listingFields,
              ...strategyField,
            } as never)
            .select("id")
            .single();

          if (insertError) throw insertError;

          if (inserted?.id) {
            const { error: histError } = await supabase
              .from("pipeline_stage_history")
              .insert({
                pipeline_property_id: inserted.id,
                changed_by: user.id,
                from_pipeline_id: null,
                to_pipeline_id: watchlist?.id ?? null,
                workspace_id: currentWorkspace.id,
              } as never);
            if (histError)
              console.error("pipeline_stage_history insert:", histError);
            else invalidateStageHistory();
          }

          toast.success("Added to pipeline");

          pipelinePropertyId =
            (inserted as { id: string } | null)?.id ?? null;
        }

        // Upsert property_setting with the caller-provided initial values
        // (used by the detail page so the user's current edits survive the
        // promotion). Done after pipeline_properties so the FK is valid.
        if (pipelinePropertyId && options?.settings) {
          const settingsErr = await upsertPropertySetting({
            pipelinePropertyId,
            workspaceId: currentWorkspace.id,
            createdBy: user.id,
            settings: options.settings,
          });
          if (settingsErr) {
            console.error("property_setting upsert:", settingsErr);
            toast.error(
              toUserMessage(
                settingsErr,
                "Added to pipeline, but failed to save deal inputs",
              ),
            );
          } else {
            void queryClient.invalidateQueries({
              queryKey: ["deal_settings", pipelinePropertyId],
            });
          }
        }

        invalidateSaved();
        return pipelinePropertyId;
      } catch (error: unknown) {
        console.error("Error adding to pipeline:", error);
        toast.error(toUserMessage(error, "Failed to add to pipeline"));
        return null;
      }
    },
    [
      user?.id,
      currentWorkspace?.id,
      savedProperties,
      pipelines,
      watchlist?.id,
      invalidateSaved,
      invalidateStageHistory,
      queryClient,
      ensureNotSold,
    ],
  );

  /**
   * Promote a saved property (heart-saved, `strategy IS NULL`) into a
   * pipeline with the user's chosen strategy. If `pipelineId` is omitted,
   * the row keeps its current `pipeline_id`.
   *
   * A `property_setting` row is seeded for the deal when it doesn't have one
   * yet, so a deal promoted from the New Deal drawer opens with the same
   * defaults as one added via "Add to Pipeline" on the detail page.
   */
  const promoteToPipeline = useCallback(
    async (args: {
      propertyId: string;
      strategy: Strategy;
      pipelineId?: string | null;
    }): Promise<boolean> => {
      if (!user?.id || !currentWorkspace?.id) {
        toast.error("Please log in to add properties to your pipeline");
        return false;
      }

      const row = savedProperties.find((p) => p.property_id === args.propertyId);
      if (!row) {
        toast.error("Save the property first, then add it to the pipeline");
        return false;
      }

      // Flow 3 of 3 — the New Deal drawer. The row already exists (heart-saved),
      // but it's only now becoming a real deal and the save may have been days
      // ago, so re-check before promoting it.
      const gate = await ensureNotSold(row.property_data);
      if (!gate.proceed) return false;

      try {
        const patch: Record<string, unknown> = { strategy: args.strategy };
        if (args.pipelineId !== undefined) patch.pipeline_id = args.pipelineId;
        if (gate.status) {
          patch.listing_status = gate.status;
          patch.listing_status_checked_at = gate.checkedAt ?? null;
        }

        const { data, error } = await supabase
          .from("pipeline_properties")
          .update(patch as never)
          .eq("id", row.id)
          .select("id");
        if (error) throw error;
        assertRowsAffected(data, "promote this property to the pipeline");

        const nextPipelineId =
          args.pipelineId !== undefined ? args.pipelineId : row.pipeline_id;
        if (nextPipelineId !== row.pipeline_id) {
          await supabase
            .from("pipeline_stage_history")
            .insert({
              pipeline_property_id: row.id,
              changed_by: user.id,
              from_pipeline_id: row.pipeline_id,
              to_pipeline_id: nextPipelineId,
              workspace_id: currentWorkspace.id,
            } as never);
          invalidateStageHistory();
        }

        toast.success(`Added to pipeline as ${args.strategy}`);

        // Seed the deal's settings row. Done after the pipeline_properties
        // write (the FK target) and reported separately so a settings failure
        // never reads as a failed promotion.
        const settingsErr = await seedPropertySetting({
          pipelinePropertyId: row.id,
          workspaceId: currentWorkspace.id,
          createdBy: user.id,
          property: row.property_data,
          strategy: args.strategy,
        });
        if (settingsErr) {
          console.error("property_setting seed:", settingsErr);
          toast.error(
            toUserMessage(
              settingsErr,
              "Added to pipeline, but failed to create deal settings",
            ),
          );
        } else {
          void queryClient.invalidateQueries({
            queryKey: ["deal_settings", row.id],
          });
          void queryClient.invalidateQueries({
            queryKey: ["deal_settings_rehab_line_items", row.id],
          });
        }

        invalidateSaved();
        return true;
      } catch (err: unknown) {
        console.error("Error promoting property:", err);
        toast.error(toUserMessage(err, "Failed to add property to pipeline"));
        return false;
      }
    },
    [
      user?.id,
      currentWorkspace?.id,
      savedProperties,
      invalidateSaved,
      invalidateStageHistory,
      queryClient,
      ensureNotSold,
    ],
  );

  return {
    savedProperties,
    savedPropertyIds,
    isLoading,
    isSaved,
    saveProperty,
    unsaveProperty,
    removeDeal,
    toggleSaved,
    addToPipeline,
    promoteToPipeline,
    /** Exposed so a caller can run the same pre-write check itself. */
    ensureNotSold,
    watchlist,
    refetch: savedPropertiesQuery.refetch,
  };
}
